import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import { API } from "../../config/api";

const CompanyListView = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API.COMPANIES);
      const companyData = response.data.results ? response.data.results : response.data;
      setCompanies(companyData);
    } catch (err) {
      console.error("Error fetching companies:", err);
      // Fallback for demo if API fails
      setError("Please ensure you are logged in to view the companies.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCompanyClick = (companyCode) => {
    navigate(`/kiosk/${companyCode}`);
  };

  if (loading) {
    return (
      <div style={styles.centerContainer}>
        <div style={styles.loader}></div>
        <p style={{ marginTop: "1rem", color: "#64748b", fontWeight: 500 }}>Loading companies...</p>
      </div>
    );
  }

  return (
    <div style={styles.pageBackground}>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Select Your Workplace</h1>
          <p style={styles.subtitle}>Choose a company below to launch the face attendance kiosk.</p>
        </div>

        {error && <div style={{ color: "#ef4444", textAlign: "center", marginBottom: "2rem" }}>{error}</div>}

        {companies.length === 0 && !error ? (
          <div style={styles.emptyState}>No companies found.</div>
        ) : (
          <div style={styles.grid}>
            {companies.map((company) => (
              <div 
                key={company.id} 
                style={styles.card}
                onClick={() => handleCompanyClick(company.company_code)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)";
                  e.currentTarget.style.borderColor = "#c7d2fe"; // indigo-200
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)";
                  e.currentTarget.style.borderColor = "transparent";
                }}
              >
                <div style={styles.cardIcon}>
                  {company.name.charAt(0).toUpperCase()}
                </div>
                <div style={styles.cardBody}>
                  <h3 style={styles.cardTitle}>{company.name}</h3>
                  <div style={styles.cardDetail}>
                    <svg style={styles.iconSVG} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>{company.company_code}</span>
                  </div>
                </div>
                <div style={styles.cardFooter}>
                  <span>Launch Kiosk</span>
                  <svg style={styles.arrowIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Premium UI Styles ---
const styles = {
  pageBackground: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    paddingTop: "4rem",
    paddingBottom: "4rem",
  },
  container: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "0 1.5rem",
  },
  header: {
    textAlign: "center",
    marginBottom: "4rem",
  },
  title: {
    fontSize: "2.5rem",
    fontWeight: "800",
    color: "#0f172a",
    marginBottom: "1rem",
    letterSpacing: "-0.025em",
  },
  subtitle: {
    fontSize: "1.125rem",
    color: "#64748b",
    maxWidth: "600px",
    margin: "0 auto",
    lineHeight: "1.6",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "2rem",
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: "1rem",
    padding: "2rem",
    cursor: "pointer",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid transparent",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  },
  cardIcon: {
    width: "72px",
    height: "72px",
    backgroundColor: "#eef2ff",
    color: "#4f46e5",
    borderRadius: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "2rem",
    fontWeight: "bold",
    marginBottom: "1.5rem",
    transition: "all 0.3s ease",
  },
  cardBody: {
    flexGrow: 1,
    width: "100%",
  },
  cardTitle: {
    fontSize: "1.25rem",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "0.5rem",
  },
  cardDetail: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    fontSize: "0.875rem",
    fontWeight: "500",
    marginBottom: "1.5rem",
    gap: "0.375rem",
  },
  iconSVG: {
    width: "16px",
    height: "16px",
    opacity: 0.7,
  },
  cardFooter: {
    width: "100%",
    paddingTop: "1.5rem",
    borderTop: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#4f46e5",
    fontWeight: "600",
    fontSize: "0.95rem",
    gap: "0.5rem",
  },
  arrowIcon: {
    width: "18px",
    height: "18px",
  },
  centerContainer: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
  },
  loader: {
    width: "40px",
    height: "40px",
    border: "3px solid #eef2ff",
    borderTopColor: "#4f46e5",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  emptyState: {
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "1.125rem",
    padding: "4rem",
    backgroundColor: "#ffffff",
    borderRadius: "1rem",
    border: "2px dashed #e2e8f0",
  }
};

// Add keyframes for loader
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
  document.head.appendChild(style);
}

export default CompanyListView;